import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { NGXLogger } from 'ngx-logger';

export type LimitPeriod = 'DAILY' | 'MONTHLY' | 'YEARLY' | 'LIFETIME';

export interface PlanUsageItemDTO {
  featureKey: string;
  label: string;
  used: number;
  limit: number;     // -1 = unlimited
  usedPct: number;
  atLimit: boolean;
  nearLimit: boolean;
  /**
   * How often this limit resets (backend prompt242). Counted-over-time limits — tally sheets,
   * consignments, stock units, AI credits, Linc chats — report DAILY/MONTHLY/YEARLY. Standing
   * amounts like users, partners and storage report LIFETIME.
   *
   * Use `periodSuffix()` to render it: LIFETIME must produce an empty string, never the word
   * "lifetime", which reads like a threat rather than a reassurance.
   */
  period?: LimitPeriod;
}

/**
 * " this month" / " today" / " this year", or '' when the limit never resets.
 *
 * Without this a customer reads "45 of 50" and cannot tell whether that is their allowance for
 * the month or for all time — which is exactly the confusion the backend period work removed.
 */
export function periodSuffix(period?: LimitPeriod): string {
  switch (period) {
    case 'DAILY': return ' today';
    case 'MONTHLY': return ' this month';
    case 'YEARLY': return ' this year';
    default: return '';
  }
}

export interface PlanUsageResponse {
  packageName: string | null;
  subscriptionEnd: string | null;
  hasActivePlan: boolean;
  features: PlanUsageItemDTO[];
}

@Injectable({ providedIn: 'root' })
export class PlanLimitService {
  private readonly logger = inject(NGXLogger);
  private readonly base = environment.apiUrl + 'api/v1/subscription/usage';

  constructor(private http: HttpClient) {}

  getUsage(): Observable<PlanUsageResponse> {
    this.logger.debug('[PlanLimitService] getUsage');
    return this.http.get<PlanUsageResponse>(this.base);
  }

  private cachedUsage$: Observable<PlanUsageResponse> | null = null;
  private cachedAt = 0;
  private static readonly CACHE_TTL_MS = 30_000;

  /** Shared/cached wrapper around getUsage() for DisableAtLimitDirective — several "Create X"
   *  buttons can render on the same page load (or the user can navigate between several create
   *  forms within the TTL window), and each shouldn't fire its own /subscription/usage request.
   *  Short TTL, not indefinite: this directive deliberately has no live-refresh (see its own
   *  comment) so a stale cache would otherwise extend how long a freed-up limit stays "disabled"
   *  looking, on top of that already-accepted tradeoff. */
  getUsageCached(): Observable<PlanUsageResponse> {
    const now = Date.now();
    if (!this.cachedUsage$ || now - this.cachedAt > PlanLimitService.CACHE_TTL_MS) {
      this.cachedAt = now;
      this.cachedUsage$ = this.getUsage().pipe(shareReplay(1));
    }
    return this.cachedUsage$;
  }

  isAtLimit(features: PlanUsageItemDTO[], key: string): boolean {
    return features.find(f => f.featureKey === key)?.atLimit ?? false;
  }

  isNearLimit(features: PlanUsageItemDTO[], key: string): boolean {
    return features.find(f => f.featureKey === key)?.nearLimit ?? false;
  }
}
